import { Upload as AntUpload } from 'antd';
import type { DraggerProps } from 'antd/es/upload';
import React from 'react';
import styled from 'styled-components';
import { IconWrap } from '../../icons/icon';
import { theme } from '../../theme/semanticTokens';
import { IconSize } from '../../types/icons/Icon.types';
import { IconType } from '../../types/icons/IconTypes.enum';
import { Typography } from '../../Typography';

export interface UploadProps
  extends Omit<DraggerProps, 'children' | 'showUploadList'> {
  label: string;
  error?: string;
}

const ZONE_COLORS = {
  default: {
    fill: theme.dime.colors.fill.secondary,
    border: theme.dime.colors.border.static,
    borderHover: theme.dime.colors.border.info,
    label: theme.dime.colors.link.primary,
    icon: theme.dime.colors.icon.link
  },
  error: {
    fill: theme.dime.colors.fill.criticalSecondary,
    border: theme.dime.colors.border.critical,
    borderHover: theme.dime.colors.border.critical,
    label: theme.dime.colors.text.critical,
    icon: theme.dime.colors.icon.critical
  }
};

export const Upload: React.FC<UploadProps> = ({
  label,
  error,
  className,
  ...props
}) => {
  const variant = error ? 'error' : 'default';

  return (
    <Container className={className} data-testid="upload">
      <StyledDragger
        {...props}
        showUploadList={false}
        $variant={variant}
        data-testid="upload-input"
      >
        <Content>
          <IconWrap
            type={IconType.AddFile}
            size={IconSize.Small}
            style={{ color: ZONE_COLORS[variant].icon }}
          />
          <Text
            variant="body1"
            color={ZONE_COLORS[variant].label}
            data-testid="upload-label"
          >
            {label}
          </Text>
        </Content>
      </StyledDragger>
      {error ? (
        <Text
          variant="body1"
          color={theme.dime.colors.text.critical}
          data-testid="upload-error"
        >
          {error}
        </Text>
      ) : null}
    </Container>
  );
};

const Text = styled(Typography)`
  && {
    line-height: ${theme.dime.fonts.lineHeights.md};
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.dime.spacing.m};
  width: 100%;
`;

const StyledDragger = styled(AntUpload.Dragger)<{
  $variant: keyof typeof ZONE_COLORS;
}>`
  && .ant-upload-drag {
    background: ${({ $variant }) => ZONE_COLORS[$variant].fill};
    border: 1px dashed ${({ $variant }) => ZONE_COLORS[$variant].border};
    border-radius: ${theme.dime.radius.m};
  }

  && .ant-upload-drag:not(.ant-upload-disabled):hover,
  && .ant-upload-drag.ant-upload-drag-hover:not(.ant-upload-disabled) {
    border-color: ${({ $variant }) => ZONE_COLORS[$variant].borderHover};
  }

  && .ant-upload-btn {
    padding: ${theme.dime.spacing['2xl']} ${theme.dime.spacing.m};
  }
`;

const Content = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.dime.spacing.m};
`;
